/**
 * The case's records by number, for every screen that names one (WS-22):
 * agents cite records by id, and a person reads "record 12" — the same
 * number in an agent's words, a draft's citation chips, the ratings, the
 * Assistant's links and the Evidence view. The workbench reads the index
 * once (`GET /workbench/{rid}/evidence/index`, on the case's version) and
 * provides it here, with a way to open a record beside the view.
 *
 * Outside a provider (the queue's preview, say) nothing is numbered: a
 * record is "a record", and opens nowhere.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { EvidenceIndexEntry } from '@/types/workbench';

export interface RecordIndex {
  /** A record's entry by its id (any case). */
  entry: (id: string) => EvidenceIndexEntry | undefined;
  /** Open a record beside the view; null where there's nowhere to open it. */
  open: ((id: string) => void) | null;
  /** The record open beside the view. */
  openId: string | null;
}

const EMPTY: RecordIndex = { entry: () => undefined, open: null, openId: null };

const RecordIndexContext = createContext<RecordIndex>(EMPTY);

interface RecordIndexProviderProps {
  index: EvidenceIndexEntry[] | null;
  open?: ((id: string) => void) | null;
  openId?: string | null;
  children: ReactNode;
}

export function RecordIndexProvider({
  index,
  open = null,
  openId = null,
  children,
}: Readonly<RecordIndexProviderProps>) {
  const byId = useMemo(() => new Map((index ?? []).map((e) => [e.id.toLowerCase(), e])), [index]);
  const value = useMemo<RecordIndex>(
    () => ({ entry: (id) => byId.get(id.toLowerCase()), open, openId }),
    [byId, open, openId]
  );
  return <RecordIndexContext.Provider value={value}>{children}</RecordIndexContext.Provider>;
}

export function useRecordIndex(): RecordIndex {
  return useContext(RecordIndexContext);
}

/** "record 12" — or "a record" where the index doesn't hold it. */
export function recordName(entry: EvidenceIndexEntry | undefined): string {
  return entry ? `record ${entry.number}` : 'a record';
}
